import { createContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User, LoginCredentials, RegisterCredentials, AuthResponseDto, Result, Permission } from '@/types';
import { Permissions } from '@/types';
import { auth } from '@/lib/auth';
import apiClient from '@/lib/axios';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Helper to transform API auth response to User type with actual permissions
 */
function transformAuthResponse(authData: AuthResponseDto): User {
  return {
    id: authData.userId,
    userName: authData.username,
    firstName: authData.firstName,
    lastName: authData.lastName,
    email: authData.email,
    phoneNumber: undefined,
    isActive: true,
    roles: [authData.roleName],
    permissions: authData.permissions || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Check if a permission array contains a specific permission
 * Supports wildcard permission (*) which grants all permissions
 */
function checkPermission(permissions: string[], permission: Permission): boolean {
  return permissions.includes(Permissions.All) || permissions.includes(permission);
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider manages authentication state and provides auth functions
 * - Loads user from localStorage on mount
 * - Provides login/logout functions
 * - Manages JWT token storage
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = auth.getUser();
    const token = auth.getToken();

    if (storedUser && token) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUser(storedUser);
    }

    setIsLoading(false);
  }, []);

  /**
   * Login user with credentials
   * Stores token and user data in localStorage
   * Throws error on failure for component to handle
   */
  const login = async (credentials: LoginCredentials): Promise<void> => {
    const response = await apiClient.post<Result<AuthResponseDto>>(
      '/authentication/login',
      credentials
    );

    // Check if login was successful
    if (!response.data.success || !response.data.data) {
      const errorMessage = response.data.errors?.join(', ') || 'Login failed';
      throw new Error(errorMessage);
    }

    const authData = response.data.data;
    const userData = transformAuthResponse(authData);

    // Store token and user
    auth.setToken(authData.token);
    auth.setUser(userData);

    setUser(userData);
  };

  /**
   * Register new user
   * Stores token and user data in localStorage on success
   * Throws error on failure for component to handle
   */
  const register = async (credentials: RegisterCredentials): Promise<void> => {
    // Default RoleId to 3 (User role) if not provided
    const registrationData = {
      ...credentials,
      RoleId: credentials.RoleId ?? 3,
    };

    const response = await apiClient.post<Result<AuthResponseDto>>(
      '/authentication/register',
      registrationData
    );

    // Check if registration was successful
    if (!response.data.success || !response.data.data) {
      const errorMessage = response.data.errors?.join(', ') || 'Registration failed';
      throw new Error(errorMessage);
    }

    const authData = response.data.data;
    const userData = transformAuthResponse(authData);

    // Store token and user
    auth.setToken(authData.token);
    auth.setUser(userData);

    setUser(userData);
  };

  /**
   * Logout user
   * Clears token and user data from localStorage
   */
  const logout = (): void => {
    auth.clear();
    setUser(null);
  };

  /**
   * Check if the current user has a specific permission
   * Returns false if user is not authenticated
   */
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (!user) return false;
    return checkPermission(user.permissions, permission);
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
